/**
 * Hook that resolves the next sequential activity and its default owner.
 * @agentic connections
 */

import { useEffect, useMemo, useState } from "react";
import { useMedplum } from "@medplum/react";
import type { Task } from "@medplum/fhirtypes";
import { getCarePlanActivityDisplay } from "@benchmark/core/constants";
import type { AppliedBusinessRule } from "@benchmark/core/resolvers";
import { resolveTaskActivityCodeByRef } from "@benchmark/core/resolvers";
import { getActivityTasks } from "@benchmark/core/services";
import { resolveActivityOwner } from "@benchmark/core/services";
import type { OwnerRef } from "@benchmark/core/services";

/**
 * Result of the useNextActivityOwner hook.
 * @agentic connections
 */
type UseNextActivityOwnerResult = {
  nextActivityCode?: string;
  nextActivityLabel?: string;
  defaultOwner?: OwnerRef;
  appliedRule?: AppliedBusinessRule;
  isResolving: boolean;
};

/**
 * Resolves the next sequential activity and its default owner for a given task.
 * @agentic connections
 */
export const useNextActivityOwner = (
  task: Task | null,
  opened: boolean,
): UseNextActivityOwnerResult => {
  const medplum = useMedplum();
  const carePlanRef = useMemo(
    () =>
      task?.basedOn?.find((ref) => ref.reference?.startsWith("CarePlan/"))
        ?.reference,
    [task],
  );
  const shouldResolve = opened && !!task && !!carePlanRef;
  const [state, setState] = useState<UseNextActivityOwnerResult>({
    isResolving: false,
  });

  useEffect(() => {
    if (!shouldResolve || !task || !carePlanRef) {
      return;
    }

    let cancelled = false;
    const resolve = async () => {
      setState((prev) => ({ ...prev, isResolving: true }));

      try {
        const carePlanId = carePlanRef.replace("CarePlan/", "");
        const carePlan = await medplum.readResource("CarePlan", carePlanId);

        if (cancelled || !carePlan.activity?.length) {
          setState({ isResolving: false });
          return;
        }

        const currentActivityCode = task.code?.coding?.[0]?.code;
        if (!currentActivityCode) {
          setState({ isResolving: false });
          return;
        }

        const tasks = carePlan.id
          ? await getActivityTasks(medplum, carePlan.id)
          : [];
        const tasksById = new Map(
          tasks.filter((t) => t.id).map((t) => [t.id!, t]),
        );

        if (cancelled) return;

        // Prefer an exact Task-reference match so multi-occurrence activities
        // (e.g. the 6/12/18-week cadenced followups, which share one code)
        // resolve to the specific task being completed rather than the first
        // occurrence. Fall back to code matching for legacy detail-based plans.
        const currentTaskRef = task.id ? `Task/${task.id}` : undefined;
        let currentIndex = currentTaskRef
          ? carePlan.activity.findIndex(
              (a) => a.reference?.reference === currentTaskRef,
            )
          : -1;

        if (currentIndex === -1) {
          currentIndex = carePlan.activity.findIndex((a) => {
            const detailCode = a.detail?.code?.coding?.[0]?.code;
            if (detailCode) return detailCode === currentActivityCode;
            return (
              resolveTaskActivityCodeByRef(
                a.reference?.reference,
                tasksById,
              ) === currentActivityCode
            );
          });
        }

        if (
          currentIndex === -1 ||
          currentIndex >= carePlan.activity.length - 1
        ) {
          setState({ isResolving: false });
          return;
        }

        const nextActivity = carePlan.activity[currentIndex + 1];
        const nextCode =
          nextActivity.detail?.code?.coding?.[0]?.code ??
          resolveTaskActivityCodeByRef(
            nextActivity.reference?.reference,
            tasksById,
          );

        if (!nextCode || cancelled) {
          setState({ isResolving: false });
          return;
        }

        const nextLabel = getCarePlanActivityDisplay(nextCode);

        const patientRef = carePlan.subject?.reference?.replace("Patient/", "");
        let defaultOwner: OwnerRef | undefined;
        if (patientRef) {
          try {
            const patient = await medplum.readResource("Patient", patientRef);
            if (!cancelled && patient) {
              defaultOwner =
                (await resolveActivityOwner(medplum, {
                  activityCode: nextCode,
                  patient,
                })) ?? undefined;
            }
          } catch {
            // Patient not found — leave defaultOwner undefined
          }
        }

        if (cancelled) return;

        setState({
          nextActivityCode: nextCode,
          nextActivityLabel: nextLabel,
          defaultOwner,
          appliedRule: defaultOwner?.appliedRule,
          isResolving: false,
        });
      } catch {
        if (!cancelled) setState({ isResolving: false });
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [carePlanRef, medplum, shouldResolve, task]);

  if (!shouldResolve) {
    return { isResolving: false };
  }

  return state;
};
